import { FC, MouseEvent, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMore from "@material-ui/icons/ExpandMore";

export type TableColumnOption = {
  id?: string;
  name: string;
  paths?: string[];
  subList?: TableColumnOption[];
};

interface TableColumnSettingsProps {
  items: TableColumnOption[];
  activeCols: string[];
  onTogglePaths: (paths: string[]) => void;
}

const collectPaths = (item: TableColumnOption): string[] => [
  ...(item.paths || []),
  ...(item.subList || []).flatMap(collectPaths),
];

const stopAccordionToggle = (event: MouseEvent) => {
  event.stopPropagation();
};

export const TableColumnSettings: FC<TableColumnSettingsProps> = ({
  items,
  activeCols,
  onTogglePaths,
}) => (
  <>
    {items.map((item) => (
      <ColumnAccordion
        key={item.id || item.name}
        item={item}
        activeCols={activeCols}
        onTogglePaths={onTogglePaths}
      />
    ))}
  </>
);

interface ColumnAccordionProps {
  item: TableColumnOption;
  activeCols: string[];
  onTogglePaths: (paths: string[]) => void;
  enableInnerStyles?: boolean;
}

const ColumnAccordion: FC<ColumnAccordionProps> = ({
  item,
  activeCols,
  onTogglePaths,
  enableInnerStyles,
}) => {
  const classes = useStyles();
  const paths = useMemo(() => collectPaths(item), [item]);
  const selectedCount = paths.filter((path) => activeCols.includes(path)).length;
  const [expanded, setExpanded] = useState(selectedCount > 0);

  return (
    <Accordion
      className={`${classes.accordionItem} ${enableInnerStyles ? classes.innerAccordionItem : ""}`}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary
        className={`${classes.accordionSummary} ${
          enableInnerStyles ? classes.innerAccordionSummary : ""
        }`}
        expandIcon={<ExpandMore />}
        aria-controls={`${item.id || item.name}-panel-content`}
        id={`${item.id || item.name}-panel-header`}
      >
        <ColumnCheckbox
          label={item.name}
          paths={paths}
          activeCols={activeCols}
          onTogglePaths={onTogglePaths}
        />
      </AccordionSummary>
      <AccordionDetails className={classes.formBox}>
        {(item.subList || []).map((child) =>
          child.subList?.length ? (
            <ColumnAccordion
              key={child.name}
              item={child}
              activeCols={activeCols}
              onTogglePaths={onTogglePaths}
              enableInnerStyles
            />
          ) : (
            <ColumnCheckbox
              key={child.name}
              label={child.name}
              paths={collectPaths(child)}
              activeCols={activeCols}
              onTogglePaths={onTogglePaths}
            />
          ),
        )}
      </AccordionDetails>
    </Accordion>
  );
};

interface ColumnCheckboxProps {
  label: string;
  paths: string[];
  activeCols: string[];
  onTogglePaths: (paths: string[]) => void;
}

const ColumnCheckbox: FC<ColumnCheckboxProps> = ({ label, paths, activeCols, onTogglePaths }) => {
  const selectedCount = paths.filter((path) => activeCols.includes(path)).length;
  const checked = paths.length > 0 && selectedCount === paths.length;
  const indeterminate = selectedCount > 0 && selectedCount < paths.length;

  return (
    <FormControlLabel
      onClick={stopAccordionToggle}
      onFocus={(event) => event.stopPropagation()}
      checked={checked}
      control={
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          onChange={() => onTogglePaths(paths)}
        />
      }
      label={<Typography>{label}</Typography>}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  formBox: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    "&.MuiAccordionDetails-root": {
      paddingLeft: theme.spacing(4),
    },
  },
  accordionItem: {
    boxShadow: "none",
    padding: theme.spacing(0, 3),
    margin: 0,
    borderTop: "#BEBFC0 1px solid",
    "&.MuiAccordion-rounded:first-child": {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
    "&.MuiAccordion-root.Mui-expanded": {
      margin: 0,
    },
    "&::before": {
      display: "none",
    },
  },
  innerAccordionItem: {
    padding: 0,
    borderTop: "none",
  },
  accordionSummary: {
    padding: 0,
  },
  innerAccordionSummary: {
    borderTop: "none",
    padding: 0,
  },
}));
